import { useGetEventsV2Query, useGetTouristicPlaces } from "@ac-expo/api";
import {
  Dates,
  eventTagsMapping,
  eventTypeMap,
  HomePageEventType,
  PaginatedResponse,
} from "@ac-expo/api-types";
import { useIsMobile } from "@ac-expo/ui-components";
import { formatEventDate, getAddressLabel } from "@ac-expo/utils";
import { PlaceType } from "@prisma/client";
import CardEvent, {
  CARD_EVENT_THUMBNAIL_SIZE,
} from "@ui/components/ui/structure/panels/CardEvent";
import SkeletonCardEvent from "@ui/components/ui/structure/panels/SkeletonCardEvent";
import { cn } from "@ui/index";
import DownloadAppsSection from "@web-app/src/components/Home/DownloadAppsSection";
import { useUserGeolocation } from "@web-app/src/context/userGeolocalisation.context";
import { useMapEvents } from "@web-app/src/hooks/useMapEvents";
import { getFileUrl } from "@web-app/src/shared/files";
import { observer } from "mobx-react-lite";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";
import { SvgWeb } from "../../../public/images/svg/SvgWeb";
import { GridIcon } from "../../icons";
import filterStore from "../../services/FilterStore";
import FilterBar from "../Filter/FilterBar";
import CardPlace from "../Place/CardPlace";
import Paginator from "./Paginator";

const EventMap = dynamic(() => import("../Map/Map"), {
  ssr: false,
});

function getActiveFilterDescription() {
  const descriptions: string[] = [];

  if (filterStore.place) {
    descriptions.push(`dans la ville de ${filterStore.place}`);
  }

  if (filterStore.selectedDateOption !== Dates.DEFAULT) {
    const dateDescription =
      filterStore.selectedDateOption === Dates.CUSTOM
        ? "à des dates personnalisées"
        : "à des dates prédéfinies";
    descriptions.push(dateDescription);
  }

  if (filterStore.selectedEventTypes.length > 0) {
    const types = filterStore.selectedEventTypes
      .map((type) => eventTypeMap[type].toLowerCase())
      .join(", ");
    descriptions.push(`pour les types d'événements ${types}`);
  }

  if (filterStore.selectedEventTags.length > 0) {
    const tags = filterStore.selectedEventTags
      .map((tag) => eventTagsMapping[tag])
      .join(", ");
    descriptions.push(`avec les tags ${tags}`);
  }

  if (filterStore.selectedPlaceTypes.length > 0) {
    descriptions.push(
      `pour les types de lieux ${filterStore.selectedPlaceTypes.join(", ")}`,
    );
  }

  if (filterStore.location !== null) {
    descriptions.push("à l'adresse recherchée");
  }

  if (filterStore.locationDistance !== null) {
    descriptions.push(`dans un rayon de ${filterStore.locationDistance} km`);
  }

  return descriptions.length === 0
    ? "aucun filtre appliqué"
    : descriptions.join(" et ");
}

const HomePage = observer(() => {
  const isMobile = useIsMobile();
  const router = useRouter();

  const { location: userGeolocation, isLoading: isGeolocationLoading } =
    useUserGeolocation();

  useEffect(() => {
    if (userGeolocation !== null) {
      const location = {
        lat: userGeolocation.latitude,
        lng: userGeolocation.longitude,
      };
      filterStore.setLocationText("Ma localisation");
      filterStore.setLocation(location);
    }

    // Only set loading to false when geolocation has finished loading
    if (!isGeolocationLoading) {
      filterStore.setLoading(false);
    }
  }, [userGeolocation, isGeolocationLoading]);

  const { data: eventsData, isLoading: isLoadingEvent } = useGetEventsV2Query(
    filterStore.filtersEvents,
    { enabled: !filterStore.showMap && !filterStore.isLoading() }
  );

  // TODO: Change in mobile as well: Use new accumulative map events hook
  const {
    events: mapEvents,
    isLoading: isLoadingMapEvents,
    isLoadingNewArea,
  } = useMapEvents({
    filters: filterStore.filtersEvents,
    enabled: filterStore.showMap,
  });

  const { data: events, count: eventsCount } = filterStore.showMap
    ? ({
        data: mapEvents ?? [],
        count: mapEvents?.length ?? 0,
      } as PaginatedResponse<HomePageEventType>)
    : (eventsData ?? { data: [], count: 0 });

  const actualIsLoadingEvent = filterStore.showMap
    ? isLoadingMapEvents
    : isLoadingEvent;

  const { data: touristicPlaces, isLoading: isLoadingPlaces } =
    useGetTouristicPlaces(filterStore.filtersPlaces);

  const pageNumbers = useMemo(() => {
    const totalPages = Math.ceil(
      (eventsCount ?? 0) / filterStore.eventsPerPage
    );

    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }, [eventsCount]);

  function handleClick(page: number) {
    if (pageNumbers.includes(page)) {
      window.scrollTo(0, 0);
      filterStore.setCurrentPage(page - 1);
    }
  }

  const renderNoData = (
    title: string,
    filterType: string,
    isLoading: boolean
  ) => {
    if (isLoading) {
      return null;
    }

    return (
      <div className="flex grow justify-center px-[50px] py-[200px] text-center">
        <p className="font-semibold text-gray-500">
          {`Aucun ${title} n'a été trouvé pour le filtre ${filterType}.`}
        </p>
      </div>
    );
  };

  // the consultation is counted by the event page itself
  const onEventCardClick = (event: HomePageEventType) => {
    // slug can be null on legacy rows created without EventSlugExtension
    if (!event.slug) return;
    router.push(`/event/${event.slug}`);
  };

  // TODO: Change in mobile as well: Simplified bounds handling with accumulative strategy
  const handleBoundsChanged = (
    bounds: google.maps.LatLngBounds,
    zoom: number
  ) => {
    filterStore.setMapBoundsAndZoom(bounds, zoom);
  };

  const renderEvents = () => {
    return events.map((event: HomePageEventType) => {
      const formattedDate = formatEventDate(
        event.dates.schedules,
        event.dates.exceptionalClosingDates
      );
      const imageUrl = getFileUrl(
        event.image?.key,
        event.image?.type,
        CARD_EVENT_THUMBNAIL_SIZE
      );

      return (
        <div key={event.id} className="flex-row-child">
          <CardEvent
            sponsored={event.isSponsored}
            date={formattedDate}
            imageUrl={imageUrl}
            eventName={event.name}
            subtitle={event.address?.name}
            location={getAddressLabel(event.address)}
            tags={[
              event.isPaidEntry ? "paid" : "free",
              event.isIndoor ? "indoor" : "",
              event.isOutdoor ? "outdoor" : "",
            ]}
            isTicketingAvailable={event.isTicketingAvailable}
            onClick={() => onEventCardClick(event)}
          />
        </div>
      );
    });
  };

  const renderPlaces = () => {
    if (touristicPlaces === undefined) return <></>;

    return touristicPlaces.map((touristicPlace, index) => (
      <div key={index} className="flex-row-child">
        <Link href={`/place/${touristicPlace.id}`}>
          <CardPlace
            city={touristicPlace.address.city}
            name={touristicPlace.name}
            location={`${touristicPlace.address.address}, ${touristicPlace.address.city} ${touristicPlace.address.zipCode}`}
            image={touristicPlace.image}
            type={touristicPlace.type as PlaceType}
          />
        </Link>
      </div>
    ));
  };

  return (
    <>
      <div className="">
        <div className="gap-6 px-[14px] pb-20 md:px-8 md:py-10 lg:px-20">
          {!isMobile ? (
            <div className="flex h-10 items-center justify-between">
              <h1 className="text-[40px] font-extrabold leading-10">
                Découvrir
              </h1>
              <button
                type="button"
                onClick={() => filterStore.setShowMap(!filterStore.showMap)}
                className="flex items-center gap-2 rounded-lg bg-blue-500 px-8 py-4"
              >
                <span className="text-[14px] font-semibold text-white">
                  {filterStore.showMap
                    ? "Revenir à la liste"
                    : "Voir sur la carte"}
                </span>
                {filterStore.showMap ? (
                  <GridIcon width={20} height={20} fill="white" />
                ) : (
                  <SvgWeb
                    name="plan"
                    isColored
                    alt="plan"
                    width={20}
                    height={20}
                  />
                )}
              </button>
            </div>
          ) : null}

          <div className="flex gap-6 border-gray-300 pt-6 md:border-b">
            <FilterBar
              startDate={filterStore.startDate}
              endDate={filterStore.endDate}
            />
          </div>

          {isMobile ? (
            <div className="flex w-full items-center justify-between gap-4 py-4">
              <p className="text-xs font-semibold text-gray-500">
                {filterStore.place && events && events.length
                  ? `${events.length} événements à : "${filterStore.place}"`
                  : ""}
              </p>
              <button
                type="button"
                onClick={() => filterStore.setShowMap(!filterStore.showMap)}
                className="flex items-center gap-2 rounded-lg bg-blue-500 px-6 py-4"
              >
                <span className="text-[14px] font-semibold text-white">
                  {filterStore.showMap
                    ? "Revenir à la liste"
                    : "Voir sur la carte"}
                </span>
                {filterStore.showMap ? (
                  <GridIcon width={20} height={20} fill="white" />
                ) : (
                  <SvgWeb
                    name="plan"
                    isColored
                    alt="plan"
                    width={20}
                    height={20}
                  />
                )}
              </button>
            </div>
          ) : null}

          {filterStore.showMap ? (
            <div className="pt-6">
              <EventMap
                events={
                  filterStore.activeData == "places" ? [] : (events ?? [])
                }
                places={
                  filterStore.activeData == "events" ? [] : touristicPlaces
                }
                onBoundsChanged={handleBoundsChanged}
                isLoadingNewArea={isLoadingNewArea}
                delay={300} // TODO: Change in mobile as well: Reduced delay for better responsiveness during drag operations
              />
            </div>
          ) : (
            <div
              className={cn(
                "row-1 md:row-2 lg:row-3 2xl:row-4 flex flex-row flex-wrap items-start gap-x-8 gap-y-6 pb-6 md:pt-8"
              )}
            >
              {actualIsLoadingEvent ||
              isLoadingPlaces ||
              filterStore.isLoading()
                ? Array.from({ length: 10 }).map((_, index) => (
                    <div key={index} className="flex-row-child w-full">
                      <SkeletonCardEvent />
                    </div>
                  ))
                : null}

              {filterStore.activeData === "events" &&
                (events && events.length > 0
                  ? renderEvents()
                  : renderNoData(
                      "événement",
                      getActiveFilterDescription(),
                      isLoadingEvent && !filterStore.isLoading()
                    ))}

              {filterStore.activeData === "places" &&
                (touristicPlaces && touristicPlaces.length > 0
                  ? renderPlaces()
                  : renderNoData(
                      "lieux",
                      getActiveFilterDescription(),
                      isLoadingPlaces
                    ))}

              {filterStore.activeData === "all" && (
                <>
                  {(events && events.length > 0) ||
                  (touristicPlaces && touristicPlaces.length > 0) ? (
                    <>
                      {renderEvents()} {renderPlaces()}
                    </>
                  ) : (
                    renderNoData(
                      "événements et lieux",
                      getActiveFilterDescription(),
                      isLoadingEvent || isLoadingPlaces
                    )
                  )}
                </>
              )}
            </div>
          )}

          {!filterStore.showMap && (
            <div className="flex flex-col items-center justify-between gap-2 border-t border-gray-300 pt-4 md:flex-row md:gap-0">
              <div className="flex grow items-center gap-2">
                <span>Afficher</span>
                <select
                  value={filterStore.eventsPerPage}
                  onChange={(e) =>
                    filterStore.setEventsPerPage(Number(e.target.value))
                  }
                  className="flex rounded-lg border border-gray-300 px-2 py-1 text-center"
                  name="events-per-page"
                >
                  <option value={28}>28</option>
                  <option value={40}>40</option>
                  <option value={60}>60</option>
                </select>
                <span className="flex whitespace-nowrap">/ page</span>
              </div>
              <Paginator pageNumbers={pageNumbers} changePage={handleClick} />
            </div>
          )}
        </div>
      </div>
      <DownloadAppsSection />
    </>
  );
});

export default HomePage;
